import {
  getDays,
  getPlaces,
  getAccommodation,
  getTravel,
  getDocuments
} from '../firebase/firestoreService';
import { LINK_TYPES, normalizeRegistryEntityType, isRegisteredEntityType } from '../config/entityTypeRegistry';
import { buildDedupKey } from '../utils/linkCanonicalUtils';
import { resolvePlaceIdFromReference } from '../utils/placeReferenceUtils';
import {
  extractFromTypedReferenceBlocks,
  isAccommodationDayItineraryBlock
} from '../utils/linkExtractorUtils';
import { connectionRegistryService } from './connectionRegistryService';
import { eventBridgeService, EVENT_TYPES } from './eventService';

function candidateKey(endpointA, endpointB, linkType) {
  return buildDedupKey(endpointA, endpointB, linkType);
}

function addCandidate(map, endpointA, endpointB, linkType, presentation, metadata) {
  const key = candidateKey(endpointA, endpointB, linkType);
  if (map.has(key)) {
    return 'duplicate';
  }
  map.set(key, {
    endpointA,
    endpointB,
    linkType,
    presentation,
    metadata
  });
  return 'added';
}

function extractFromAccommodationPlaceReference(accommodation, map) {
  const placeId = resolvePlaceIdFromReference(accommodation.placeReference);
  if (!placeId) {
    return;
  }
  addCandidate(
    map,
    { type: 'accommodation', id: accommodation.id },
    { type: 'place', id: placeId },
    LINK_TYPES.LOCATED_IN,
    { linkIntent: 'selected', displayMode: 'related_only' },
    { legacySource: 'place_reference', createdBy: 'extractor' }
  );
}

function extractFromPlaceInverseBlocks(place, map) {
  const blocks = place.contentBlocks || [];
  blocks.forEach((block) => {
    if (block.sourceEntityType === 'accommodation' && block.sourceEntityId) {
      addCandidate(
        map,
        { type: 'accommodation', id: block.sourceEntityId },
        { type: 'place', id: place.id },
        LINK_TYPES.LOCATED_IN,
        { linkIntent: 'selected', displayMode: 'related_only' },
        { legacySource: 'place_inverse_block', createdBy: 'extractor' }
      );
    }
  });
}

function extractFromDayContent(day, map) {
  const blocks = day.content || [];
  blocks.forEach((block) => {
    if (block.type === 'accommodation_reference' && block.accommodationId) {
      addCandidate(
        map,
        { type: 'accommodation', id: block.accommodationId },
        { type: 'day', id: day.id },
        LINK_TYPES.STAYS_ON,
        {
          linkIntent: 'selected',
          displayMode: 'itinerary_entry',
          displayContext: block.context || null
        },
        { legacySource: 'typed_content_block', createdBy: 'extractor' }
      );
    }
  });
}

function extractFromAccommodationContentBlocks(accommodation, map) {
  const blocks = accommodation.contentBlocks || [];
  blocks.forEach((block) => {
    if (!isAccommodationDayItineraryBlock(block)) {
      return;
    }
    addCandidate(
      map,
      { type: 'accommodation', id: accommodation.id },
      { type: 'day', id: block.dayId },
      LINK_TYPES.STAYS_ON,
      {
        linkIntent: 'selected',
        displayMode: 'itinerary_entry',
        displayContext: block.context || null
      },
      { legacySource: 'typed_content_block', createdBy: 'extractor' }
    );
  });
}

function extractFromEntityContentBlocks(hostEntityType, hostEntityId, blocks, map) {
  extractFromTypedReferenceBlocks(hostEntityType, hostEntityId, blocks, map, addCandidate);
}

function extractFromDayLinks(accommodation, map) {
  const dayLinks = accommodation.dayLinks || [];
  dayLinks.forEach((entry) => {
    const dayId = typeof entry === 'string' ? entry : entry?.dayId;
    if (!dayId) {
      return;
    }
    addCandidate(
      map,
      { type: 'accommodation', id: accommodation.id },
      { type: 'day', id: dayId },
      LINK_TYPES.STAYS_ON,
      {
        linkIntent: 'selected',
        displayMode: 'itinerary_entry',
        displayContext: typeof entry === 'object' ? entry.context || null : null
      },
      { legacySource: 'day_links', createdBy: 'extractor' }
    );
  });
}

function extractFromDocumentConnections(document, map, unmapped) {
  const connections = document.connections || [];
  connections.forEach((connection) => {
    if (!connection?.entityType || !connection?.entityId) {
      return;
    }
    const entityType = normalizeRegistryEntityType(connection.entityType);
    if (!isRegisteredEntityType(entityType)) {
      unmapped.push({
        source: 'document_connection',
        documentId: document.id,
        entityType: connection.entityType,
        entityId: connection.entityId,
        reason: 'unsupported entity type'
      });
      return;
    }
    addCandidate(
      map,
      { type: 'document', id: document.id },
      { type: entityType, id: connection.entityId },
      LINK_TYPES.DOCUMENT_FOR,
      { linkIntent: 'selected', displayMode: 'related_only' },
      {
        legacySource: 'document_connection',
        createdBy: 'extractor',
        connectionType: connection.connectionType || 'related',
        ...(connection.entityType !== entityType
          ? { originalEntityType: connection.entityType }
          : {})
      }
    );
  });
}

function extractFromTravelPlaceFields(travelItem, map) {
  if (travelItem.departurePlace) {
    addCandidate(
      map,
      { type: 'travel', id: travelItem.id },
      { type: 'place', id: travelItem.departurePlace },
      LINK_TYPES.DEPARTS_FROM,
      { linkIntent: 'selected', displayMode: 'related_only' },
      { legacySource: 'travel_place_field', createdBy: 'extractor' }
    );
  }
  if (travelItem.arrivalPlace) {
    addCandidate(
      map,
      { type: 'travel', id: travelItem.id },
      { type: 'place', id: travelItem.arrivalPlace },
      LINK_TYPES.ARRIVES_AT,
      { linkIntent: 'selected', displayMode: 'related_only' },
      { legacySource: 'travel_place_field', createdBy: 'extractor' }
    );
  }
}

export const linkExtractorService = {
  async scanLegacyCandidates(userId, tripId) {
    const [days, places, accommodations, travelItems, documents] = await Promise.all([
      getDays(userId, tripId),
      getPlaces(userId, tripId),
      getAccommodation(userId, tripId),
      getTravel(userId, tripId),
      getDocuments(userId, tripId)
    ]);

    const map = new Map();
    const unmapped = [];

    accommodations.forEach((accommodation) => {
      extractFromAccommodationPlaceReference(accommodation, map);
      extractFromAccommodationContentBlocks(accommodation, map);
      extractFromDayLinks(accommodation, map);
      extractFromEntityContentBlocks('accommodation', accommodation.id, accommodation.contentBlocks, map);
    });

    places.forEach((place) => {
      extractFromPlaceInverseBlocks(place, map);
      extractFromEntityContentBlocks('place', place.id, place.contentBlocks, map);
    });
    days.forEach((day) => {
      extractFromDayContent(day, map);
      extractFromEntityContentBlocks('day', day.id, day.content, map);
    });
    documents.forEach((document) => extractFromDocumentConnections(document, map, unmapped));
    travelItems.forEach((travelItem) => {
      extractFromTravelPlaceFields(travelItem, map);
      extractFromEntityContentBlocks('travel', travelItem.id, travelItem.contentBlocks, map);
    });

    return {
      toCreate: Array.from(map.values()),
      duplicates: [],
      orphans: [],
      unmapped
    };
  },

  async extractLinksFromLegacy(userId, tripId, { dryRun = false } = {}) {
    const scan = await this.scanLegacyCandidates(userId, tripId);
    const duplicates = [];
    const created = [];
    const errors = [];

    for (const candidate of scan.toCreate) {
      try {
        if (dryRun) {
          const exists = await connectionRegistryService.linkExists(
            userId,
            tripId,
            candidate.endpointA,
            candidate.endpointB,
            candidate.linkType
          );
          if (exists) {
            duplicates.push(candidate);
          } else {
            created.push(candidate);
          }
          continue;
        }

        const alreadyExists = await connectionRegistryService.linkExists(
          userId,
          tripId,
          candidate.endpointA,
          candidate.endpointB,
          candidate.linkType
        );
        if (alreadyExists) {
          duplicates.push(candidate);
          continue;
        }

        const result = await connectionRegistryService.createLink(
          userId,
          tripId,
          candidate.endpointA,
          candidate.endpointB,
          {
            linkType: candidate.linkType,
            linkIntent: candidate.presentation.linkIntent,
            displayMode: candidate.presentation.displayMode,
            displayContext: candidate.presentation.displayContext,
            metadata: candidate.metadata
          }
        );

        if (result.deduplicated) {
          duplicates.push(candidate);
        } else {
          created.push({ ...candidate, linkId: result.linkId });
        }
      } catch (error) {
        errors.push({ candidate, error: error.message });
      }
    }

    const stats = {
      linksCreated: created.length,
      duplicatesSkipped: duplicates.length,
      errors: errors.length
    };

    if (!dryRun) {
      await connectionRegistryService.setLinkRegistryConfig(userId, tripId, {
        extractorVersion: new Date().toISOString(),
        extractorStats: stats
      });

      eventBridgeService.publish(EVENT_TYPES.LINK_REGISTRY_EXTRACT_COMPLETED, {
        userId,
        tripId,
        stats,
        dryRun: false,
        timestamp: new Date().toISOString()
      });
    }

    return {
      toCreate: created,
      duplicates,
      orphans: scan.orphans,
      unmapped: scan.unmapped,
      errors,
      stats
    };
  }
};

export default linkExtractorService;
